import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { instructorApi } from '../api/instructors';
import { cohortApi } from '../api/cohorts';
import { Instructor } from '../types/instructor.types';

interface Cohort {
  id: string;
  name: string;
  canvasCourseId: string;
  standardReference: string;
}

export const InstructorsList: React.FC = () => {
  const { user } = useAuth();
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [selectedCohortId, setSelectedCohortId] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<any>(null);
  const [formData, setFormData] = useState({
    canvas_user_id: '',
    first_name: '',
    last_name: '',
    email: '',
    canvas_course_id: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    if (!user?.tenant_id) return;

    try {
      setLoading(true);
      const [instructorsRes, cohortsRes] = await Promise.all([
        instructorApi.getAll(user.tenant_id),
        cohortApi.getAll(user.tenant_id),
      ]);
      setInstructors(instructorsRes.data);
      setCohorts(cohortsRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.tenant_id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.tenant_id) return;

    try {
      setSaving(true);
      setError('');
      await instructorApi.create({
        tenant_id: user.tenant_id,
        ...formData,
      });
      await loadData();
      setShowAddModal(false);
      setFormData({
        canvas_user_id: '',
        first_name: '',
        last_name: '',
        email: '',
        canvas_course_id: '',
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add instructor');
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    if (!user?.tenant_id || !selectedCohortId) {
      setError('Please select a course');
      return;
    }

    const selectedCohort = cohorts.find(c => c.id === selectedCohortId);
    if (!selectedCohort || !selectedCohort.canvasCourseId) {
      setError('Selected course does not have a Canvas Course ID');
      return;
    }

    try {
      setSyncing(true);
      setError('');
      setSyncResult(null);
      
      const response = await instructorApi.syncFromCanvas(
        user.tenant_id,
        selectedCohort.canvasCourseId
      );
      
      setSyncResult(response.data);
      await loadData();
      
      if (response.data.success) {
        setTimeout(() => {
          setShowSyncModal(false);
          setSelectedCohortId('');
          setSyncResult(null);
        }, 3000);
      }
    } catch (err: any) {
      console.error('Sync error:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to sync from Canvas');
      setSyncResult(err.response?.data);
    } finally {
      setSyncing(false);
    }
  };

  const handleDeactivate = async (id: string) => {
    if (!user?.tenant_id) return;
    if (!window.confirm('Are you sure you want to deactivate this instructor?')) return;

    try {
      await instructorApi.deactivate(user.tenant_id, id);
      await loadData();
    } catch (err) {
      console.error('Failed to deactivate instructor:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading instructors...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Assessors</h1>
          <p className="text-gray-600">{instructors.length} assessors in the system</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowSyncModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition"
            disabled={cohorts.length === 0}
          >
            Sync from Canvas
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
          >
            Add Manually
          </button>
        </div>
      </div>

      {cohorts.length === 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-orange-800">
            <strong>Note:</strong> No apprenticeship courses found. Please create a course first before syncing assessors.
          </p>
        </div>
      )}

      {instructors.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-600 mb-4">No assessors added yet.</p>
          <div className="flex justify-center gap-3">
            {cohorts.length > 0 && (
              <button
                onClick={() => setShowSyncModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition"
              >
                Sync from Canvas
              </button>
            )}
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            >
              Add Manually
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Canvas User ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {instructors.map((instructor) => (
                <tr key={instructor.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{instructor.first_name} {instructor.last_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{instructor.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-mono text-xs">
                    {instructor.canvas_user_id.substring(0, 8)}...
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{instructor.canvas_course_id || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      instructor.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {instructor.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {instructor.is_active && (
                      <button onClick={() => handleDeactivate(instructor.id)} className="text-red-600 hover:text-red-900 font-medium">
                        Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showSyncModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold mb-4">Sync Assessors from Canvas</h3>
            <p className="text-sm text-gray-600 mb-4">
              Select an apprenticeship course to sync all teachers and TAs enrolled in that Canvas course.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Apprenticeship Course *</label>
              <select
                value={selectedCohortId}
                onChange={(e) => setSelectedCohortId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={syncing}
              >
                <option value="">-- Select a course --</option>
                {cohorts.map((cohort) => (
                  <option key={cohort.id} value={cohort.id}>
                    {cohort.name} {cohort.canvasCourseId ? `(Canvas: ${cohort.canvasCourseId})` : ''}
                  </option>
                ))}
              </select>
              {cohorts.length === 0 && (
                <p className="mt-2 text-sm text-orange-600">No apprenticeship courses available. Please create a course first.</p>
              )}
            </div>

            {syncResult && (
              <div className={`mb-4 p-3 rounded-md ${
                syncResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`}>
                {syncResult.success ? (
                  <div className="text-sm text-green-800">
                    <p className="font-semibold">✓ Sync completed successfully!</p>
                    <p className="mt-1">Synced {syncResult.synced} assessors</p>
                    {syncResult.errors > 0 && (
                      <p className="mt-1 text-orange-600">{syncResult.errors} errors occurred</p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-red-800">{syncResult.error || 'Sync failed'}</p>
                )}
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowSyncModal(false);
                  setSelectedCohortId('');
                  setSyncResult(null);
                  setError('');
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={syncing}
              >
                Close
              </button>
              <button
                onClick={handleSync}
                disabled={syncing || !selectedCohortId || cohorts.length === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400"
              >
                {syncing ? 'Syncing...' : 'Sync from Canvas'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold mb-4">Add Assessor Manually</h3>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Canvas User ID *</label>
                  <input
                    type="text"
                    required
                    value={formData.canvas_user_id}
                    onChange={(e) => setFormData({ ...formData, canvas_user_id: e.target.value })}
                    placeholder="UUID from Canvas"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setError('');
                    setFormData({ canvas_user_id: '', first_name: '', last_name: '', email: '', canvas_course_id: '' });
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {saving ? 'Adding...' : 'Add Assessor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
